import { useCallback, useEffect, useState } from 'react';

/**
 * Light or dark, remembered between visits.
 *
 * The design ships both palettes, and every colour on every screen comes from
 * a token — so this writes one attribute on `<html>` and the whole admin
 * follows. Nothing here knows which components exist.
 */
export type Theme = 'dark' | 'light';

const KEY = 'sonasi.theme';

/**
 * Dark unless the person said otherwise.
 *
 * Not `prefers-color-scheme`: the studio's own default is dark, and reading the
 * operating system would flip the admin to light for anybody whose laptop is
 * light — a change they never asked for, on a screen they use all day. Their
 * own choice is what is stored, and it is the only thing consulted.
 */
export const storedTheme = (): Theme => {
  try {
    return localStorage.getItem(KEY) === 'light' ? 'light' : 'dark';
  } catch {
    // Private windows and blocked site data throw on access rather than
    // returning null. Dark is the default anyway.
    return 'dark';
  }
};

export const applyTheme = (theme: Theme): void => {
  document.documentElement.dataset.theme = theme;
};

export const useTheme = (): { theme: Theme; toggle: () => void } => {
  const [theme, setTheme] = useState<Theme>(storedTheme);

  useEffect(() => {
    applyTheme(theme);
    try {
      localStorage.setItem(KEY, theme);
    } catch {
      // Nothing to do: the choice holds for this tab and is forgotten after.
    }
  }, [theme]);

  const toggle = useCallback(() => setTheme((now) => (now === 'light' ? 'dark' : 'light')), []);

  return { theme, toggle };
};
